import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Factory, LogOut, Sun, Moon, Package, TrendingUp, CheckCircle2, FlaskConical, Clock, ShieldCheck, Sparkles, Layers, Heart, GitCompareArrows, X, Lock, Check, Inbox, Bell } from "lucide-react";
import { useList } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useRealtime } from "@/context/RealtimeContext";
import { Loading, EmptyState, StatusBadge, ProgressBar, fmt } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CompareDialog, getFavs, setFavsLS } from "@/components/DevLibrary";
import { RestockCatalogue, MyRestockRequests } from "@/components/BuyerRestock";
import { UpdatesFeed, UpdateDetail, PriorityBadge } from "@/components/BuyerUpdates";
import { PackageCheck, ClipboardList } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Lightbox, Thumb } from "@/components/Lightbox";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function Kpi({ icon: Icon, label, value, suffix, tone }) {
  const t = { success: "text-success", warning: "text-warning", primary: "text-primary" }[tone] || "text-foreground";
  const bg = { success: "bg-success/12 text-success", warning: "bg-warning/12 text-warning", primary: "bg-primary/12 text-primary" }[tone] || "bg-secondary text-muted-foreground";
  return (
    <Card className="kpi-card p-4 flex items-center gap-3" data-testid={`portal-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center shrink-0", bg)}><Icon className="w-5 h-5" /></div>
      <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
        <div className={cn("font-num font-bold text-2xl", t)}>{value}{suffix}</div></div>
    </Card>
  );
}

export default function BuyerPortal() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { connected } = useRealtime();
  const navigate = useNavigate();
  const { data, isLoading } = useList("portal", "/portal");
  const { data: myReqs } = useList("portal-myreq", "/my-dev-requests");
  const myUnread = (myReqs || []).filter((r) => r.buyer_unread && r.replies?.length).length;
  const [photo, setPhoto] = useState(null);
  const k = data?.kpis;

  return (
    <div className="min-h-screen bg-background">
      <header className="h-16 border-b border-border bg-card/70 backdrop-blur-md sticky top-0 z-30 px-4 sm:px-6 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center"><Factory className="w-5 h-5 text-primary-foreground" /></div>
          <div>
            <div className="font-display font-bold text-lg leading-none tracking-wide">TEXFLOW</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Buyer Portal</div>
          </div>
          <span className="ml-3 flex items-center gap-1.5 text-xs font-semibold">
            <span className={cn("live-dot w-2.5 h-2.5 rounded-full", connected ? "bg-success" : "bg-muted-foreground")} />
            <span className={connected ? "text-success" : "text-muted-foreground"}>{connected ? "LIVE" : "OFFLINE"}</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden sm:inline text-sm font-medium mr-1">{user?.name || user?.email}</span>
          <span className="hidden sm:inline text-[10px] uppercase tracking-wider text-primary font-semibold px-2 py-0.5 rounded bg-primary/10">Buyer · Read-only</span>
          <NotificationsBell />
          <Button variant="ghost" size="icon" onClick={toggle} data-testid="portal-theme-toggle">{theme === "dark" ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}</Button>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="portal-logout-btn"><LogOut className="w-[18px] h-[18px]" /></Button>
        </div>
      </header>

      <main className="p-4 sm:p-6 max-w-[1300px] mx-auto fade-in">
        {isLoading ? <Loading /> : (
          <>
            <div className="mb-6">
              <h1 className="text-3xl sm:text-4xl font-bold uppercase tracking-tight">Welcome{data?.linked ? `, ${data.linked}` : ""}</h1>
              <p className="text-sm text-muted-foreground mt-1">Live status of your orders and sample approvals — updates automatically.</p>
            </div>

            <PortalSpotlight wmLabel={data?.linked || user?.name || user?.email} />

            {!data?.linked && user?.role !== "admin" ? (
              <Card className="p-8"><EmptyState title="Your account isn't linked to a buyer yet" hint="Please ask the factory administrator to link your account to your company so your orders appear here." testid="portal-unlinked" /></Card>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 mb-6">
                  <Kpi icon={Package} label="Total Orders" value={fmt(k?.total_orders)} tone="primary" />
                  <Kpi icon={TrendingUp} label="In Production" value={fmt(k?.in_production)} tone="primary" />
                  <Kpi icon={CheckCircle2} label="Completed" value={fmt(k?.completed)} tone="success" />
                  <Kpi icon={TrendingUp} label="Avg Completion" value={fmt(k?.avg_completion)} suffix="%" tone="primary" />
                  <Kpi icon={Clock} label="Samples Pending" value={fmt(k?.samples_awaiting)} tone="warning" />
                  <Kpi icon={ShieldCheck} label="Samples Approved" value={fmt(k?.samples_approved)} tone="success" />
                </div>

                <Tabs defaultValue="orders">
                  <TabsList className="mb-4">
                    <TabsTrigger value="orders" data-testid="portal-tab-orders"><Package className="w-4 h-4 mr-1.5" /> My Orders</TabsTrigger>
                    <TabsTrigger value="samples" data-testid="portal-tab-samples"><FlaskConical className="w-4 h-4 mr-1.5" /> My Samples</TabsTrigger>
                    <TabsTrigger value="innovation" data-testid="portal-tab-innovation"><Sparkles className="w-4 h-4 mr-1.5" /> Innovation Lab</TabsTrigger>
                    <TabsTrigger value="fabrics" data-testid="portal-tab-fabrics"><Layers className="w-4 h-4 mr-1.5" /> New Fabrics</TabsTrigger>
                    <TabsTrigger value="requests" data-testid="portal-tab-requests"><Inbox className="w-4 h-4 mr-1.5" /> My Requests{myUnread > 0 && <span className="ml-1.5 text-[10px] font-bold bg-destructive text-destructive-foreground rounded-full px-1.5 py-0.5" data-testid="portal-myreq-badge">{myUnread}</span>}</TabsTrigger>
                    <TabsTrigger value="restock" data-testid="portal-tab-restock"><PackageCheck className="w-4 h-4 mr-1.5" /> Restock Catalogue</TabsTrigger>
                    <TabsTrigger value="my-restock" data-testid="portal-tab-my-restock"><ClipboardList className="w-4 h-4 mr-1.5" /> My Restock</TabsTrigger>
                    <TabsTrigger value="updates" data-testid="portal-tab-updates"><Bell className="w-4 h-4 mr-1.5" /> Updates</TabsTrigger>
                  </TabsList>

                  <TabsContent value="orders">
                    <Card className="overflow-hidden">
                      {!data?.orders?.length ? <EmptyState title="No orders yet" /> : (
                        <div className="overflow-x-auto"><Table>
                          <TableHeader><TableRow>
                            <TableHead>Order</TableHead><TableHead>Photo</TableHead><TableHead>Product</TableHead><TableHead className="text-right">Qty</TableHead>
                            <TableHead className="w-44">Progress</TableHead><TableHead>Stage</TableHead><TableHead>Delivery</TableHead><TableHead>Status</TableHead>
                          </TableRow></TableHeader>
                          <TableBody>{data.orders.map((o) => (
                            <TableRow key={o.order_id} data-testid={`portal-order-${o.order_id}`}>
                              <TableCell className="font-num font-semibold text-primary">{o.order_id}</TableCell>
                              <TableCell><Thumb src={(o.images && o.images[0]) || o.product_image} testid={`order-thumb-${o.order_id}`} onClick={() => setPhoto({ images: (o.images && o.images.length ? o.images : [o.product_image]).filter(Boolean), title: `${o.order_id} · ${o.product_name}`, subtitle: [o.sku, o.color].filter(Boolean).join(" · ") })} /></TableCell>
                              <TableCell className="max-w-[200px]"><div className="flex items-center gap-2">
                                {o.product_image ? <img src={o.product_image} alt="" className="w-8 h-8 rounded object-cover" referrerPolicy="no-referrer" /> : <div className="w-8 h-8 rounded bg-secondary" />}
                                <div className="truncate"><div className="truncate">{o.product_name}</div><div className="text-xs text-muted-foreground">{o.sku}</div></div>
                              </div></TableCell>
                              <TableCell className="text-right font-num">{fmt(o.order_quantity)}</TableCell>
                              <TableCell><div className="flex items-center gap-2"><ProgressBar value={o.completion_pct} /><span className="text-xs font-num w-10 text-right">{o.completion_pct}%</span></div></TableCell>
                              <TableCell className="text-sm">{o.current_stage || "—"}</TableCell>
                              <TableCell className="text-sm">{o.delivery_date || "—"}</TableCell>
                              <TableCell><StatusBadge status={o.status} /></TableCell>
                            </TableRow>
                          ))}</TableBody>
                        </Table></div>
                      )}
                    </Card>
                  </TabsContent>

                  <TabsContent value="samples">
                    <Card className="overflow-hidden">
                      {!data?.samples?.length ? <EmptyState title="No samples yet" /> : (
                        <div className="overflow-x-auto"><Table>
                          <TableHeader><TableRow>
                            <TableHead>Sample</TableHead><TableHead>Photo</TableHead><TableHead>Product</TableHead><TableHead>Type</TableHead>
                            <TableHead>Rev</TableHead><TableHead>Stage</TableHead><TableHead>Required</TableHead><TableHead>Approval</TableHead>
                          </TableRow></TableHeader>
                          <TableBody>{data.samples.map((s) => (
                            <TableRow key={s.sample_id} data-testid={`portal-sample-${s.sample_id}`}>
                              <TableCell className="font-num font-semibold text-primary">{s.sample_id}</TableCell>
                              <TableCell><Thumb src={(s.images || [])[0]} testid={`sample-thumb-${s.sample_id}`} onClick={() => setPhoto({ images: s.images || [], title: `${s.sample_id} · ${s.product_name}`, subtitle: [s.sample_type, s.fabric_type, s.fabric_color].filter(Boolean).join(" · ") })} /></TableCell>
                              <TableCell className="max-w-[180px] truncate">{s.product_name}</TableCell>
                              <TableCell className="text-sm">{s.sample_type}</TableCell>
                              <TableCell className="font-num">V{s.revision_number}</TableCell>
                              <TableCell><StatusBadge status={s.status} /></TableCell>
                              <TableCell className="text-sm">{s.required_completion_date || "—"}</TableCell>
                              <TableCell><StatusBadge status={s.approval_status} /></TableCell>
                            </TableRow>
                          ))}</TableBody>
                        </Table></div>
                      )}
                    </Card>
                  </TabsContent>

                  <TabsContent value="innovation">
                    <PortalDevGallery prefix="innovation" empty="No new inks published yet" wmLabel={data?.linked || user?.name || user?.email} />
                  </TabsContent>
                  <TabsContent value="fabrics">
                    <PortalDevGallery prefix="fabrics" empty="No new fabrics published yet" wmLabel={data?.linked || user?.name || user?.email} />
                  </TabsContent>
                  <TabsContent value="requests">
                    <PortalMyRequests />
                  </TabsContent>
                  <TabsContent value="restock">
                    <RestockCatalogue />
                  </TabsContent>
                  <TabsContent value="my-restock">
                    <MyRestockRequests />
                  </TabsContent>
                  <TabsContent value="updates">
                    <UpdatesFeed />
                  </TabsContent>
                </Tabs>

                {user?.role === "admin" && (
                  <p className="text-xs text-muted-foreground mt-4">You are viewing as admin. Buyers see only orders/samples for their linked company. <button className="text-primary underline" onClick={() => navigate("/")}>Back to full dashboard</button></p>
                )}
              </>
            )}
          </>
        )}
      </main>
      {photo && <Lightbox images={photo.images} title={photo.title} subtitle={photo.subtitle} onClose={() => setPhoto(null)} />}
    </div>
  );
}

function Watermark({ label }) {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='260' height='150'><text x='12' y='90' fill='rgba(255,255,255,0.42)' stroke='rgba(0,0,0,0.28)' stroke-width='0.6' paint-order='stroke' font-size='13' font-weight='bold' font-family='sans-serif' transform='rotate(-24 130 75)'>${label} · CONFIDENTIAL</text></svg>`;
  const uri = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  return <div className="absolute inset-0 pointer-events-none z-10" style={{ backgroundImage: `url("${uri}")`, backgroundRepeat: "repeat" }} data-testid="portal-watermark" />;
}

function PortalDevGallery({ prefix, empty, wmLabel }) {
  const { data: items, isLoading } = useList(`portal-${prefix}`, `/${prefix}`);
  const [detailId, setDetailId] = useState(null);
  const [cmpMode, setCmpMode] = useState(false);
  const [sel, setSel] = useState([]);
  const [showCmp, setShowCmp] = useState(false);
  const detail = detailId ? (items || []).find((x) => x.id === detailId) : null;
  const selItems = sel.map((id) => (items || []).find((x) => x.id === id)).filter(Boolean);

  const onCard = (it) => {
    if (!cmpMode) return setDetailId(it.id);
    setSel((s) => s.includes(it.id) ? s.filter((x) => x !== it.id) : (s.length >= 2 ? [s[1], it.id] : [...s, it.id]));
  };

  if (isLoading) return <Loading />;
  if (!items?.length) return <Card className="p-8"><EmptyState title={empty} hint="Our R&D team publishes new developments here — check back soon." testid={`portal-${prefix}-empty`} /></Card>;
  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-muted-foreground">{cmpMode ? <>Select up to 2 to compare — <span className="font-semibold text-foreground">{sel.length}/2</span></> : `${items.length} published development${items.length !== 1 ? "s" : ""}`}</div>
        <Button size="sm" variant={cmpMode ? "default" : "outline"} onClick={() => { setCmpMode((m) => !m); setSel([]); }} className="gap-1.5" data-testid={`portal-${prefix}-compare-toggle`}><GitCompareArrows className="w-4 h-4" /> {cmpMode ? "Exit Compare" : "Compare"}</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid={`portal-${prefix}-grid`}>
        {items.map((it) => {
          const thumb = it.thumbnail || it.images?.[0];
          const picked = sel.includes(it.id);
          return (
            <Card key={it.id} className={cn("kpi-card overflow-hidden cursor-pointer group relative", picked && "ring-2 ring-primary")} onClick={() => onCard(it)} data-testid={`portal-${prefix}-card-${it.dev_id}`}>
              {cmpMode && <div className={cn("absolute top-2 left-2 z-20 w-6 h-6 rounded-full border-2 flex items-center justify-center", picked ? "bg-primary border-primary text-primary-foreground" : "bg-black/40 border-white/70 text-transparent")}><Check className="w-3.5 h-3.5" /></div>}
              <div className="relative aspect-[4/3] bg-secondary overflow-hidden">
                {thumb ? <img src={thumb} alt="" draggable={false} onContextMenu={it.confidential ? (e) => e.preventDefault() : undefined} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" /> : <div className="w-full h-full flex items-center justify-center"><FlaskConical className="w-10 h-10 text-muted-foreground" /></div>}
                {it.confidential && <Watermark label={wmLabel} />}
                {it.confidential && <span className="absolute top-2 right-2 z-20 text-[10px] font-bold bg-destructive text-destructive-foreground px-2 py-0.5 rounded-full flex items-center gap-1"><Lock className="w-3 h-3" /> CONFIDENTIAL</span>}
              </div>
              <div className="p-3">
                <div className="text-[10px] font-num text-muted-foreground">{it.dev_id}</div>
                <div className="font-semibold text-sm truncate">{it.name}</div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-muted-foreground">{it.category}</span>
                  <StatusBadge status={it.status} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      {cmpMode && sel.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-card border border-border shadow-2xl rounded-full pl-4 pr-2 py-2 flex items-center gap-3" data-testid={`portal-${prefix}-compare-bar`}>
          <span className="text-sm font-medium">{sel.length} selected</span>
          <Button size="sm" variant="ghost" onClick={() => setSel([])} className="gap-1"><X className="w-4 h-4" /> Clear</Button>
          <Button size="sm" disabled={sel.length !== 2} onClick={() => setShowCmp(true)} className="gap-1 rounded-full" data-testid={`portal-${prefix}-compare-open`}><GitCompareArrows className="w-4 h-4" /> Compare</Button>
        </div>
      )}
      {detail && <PortalDevDetail prefix={prefix} it={detail} wmLabel={wmLabel} onClose={() => setDetailId(null)} />}
      {showCmp && selItems.length === 2 && <CompareDialog a={selItems[0]} b={selItems[1]} onClose={() => setShowCmp(false)} />}
    </>
  );
}

function PortalDevDetail({ prefix, it, wmLabel, onClose }) {
  const [big, setBig] = useState(it.thumbnail || it.images?.[0]);
  const [fav, setFav] = useState(getFavs(prefix).includes(it.id));
  const [req, setReq] = useState(null);
  const viewSent = useRef(false);
  const specs = it.specs || {};

  useEffect(() => { if (viewSent.current) return; viewSent.current = true; api.post(`/${prefix}/${it.id}/track`, { action: "view" }).catch(() => {}); }, [prefix, it.id]);

  const toggleFav = async () => {
    const cur = getFavs(prefix);
    const next = fav ? cur.filter((x) => x !== it.id) : [...cur, it.id];
    setFavsLS(prefix, next); setFav(!fav);
    api.post(`/${prefix}/${it.id}/track`, { action: fav ? "unfavorite" : "favorite" }).catch(() => {});
    toast.success(fav ? "Removed from favorites" : "Saved to favorites");
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="portal-dev-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap"><span className="font-num text-primary">{it.dev_id}</span> {it.name} <StatusBadge status={it.status} />{it.confidential && <span className="text-[10px] font-bold bg-destructive text-destructive-foreground px-2 py-0.5 rounded-full flex items-center gap-1"><Lock className="w-3 h-3" /> CONFIDENTIAL</span>}</DialogTitle>
          <DialogDescription>{it.category} development — specs and visuals.</DialogDescription>
        </DialogHeader>
        <div className="relative aspect-video bg-secondary rounded-lg overflow-hidden flex items-center justify-center">
          {big ? <img src={big} alt="" draggable={false} onContextMenu={it.confidential ? (e) => e.preventDefault() : undefined} className="max-h-full max-w-full object-contain" /> : <FlaskConical className="w-12 h-12 text-muted-foreground" />}
          {it.confidential && <Watermark label={wmLabel} />}
        </div>
        {it.images?.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {it.images.map((u, i) => <img key={i} src={u} alt="" onClick={() => setBig(u)} className={`w-16 h-16 rounded object-cover cursor-pointer shrink-0 ${big === u ? "ring-2 ring-primary" : ""}`} />)}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={toggleFav} className="gap-1" data-testid="portal-dev-fav"><Heart className={`w-4 h-4 ${fav ? "fill-red-500 text-red-500" : ""}`} /> {fav ? "Saved" : "Save to Favorites"}</Button>
          <Button variant="outline" size="sm" onClick={() => setReq("sample")} data-testid="portal-request-sample">Request Sample</Button>
          <Button variant="outline" size="sm" onClick={() => setReq("question")} data-testid="portal-ask-question">Ask Technical Question</Button>
        </div>
        {it.description && <p className="text-sm text-muted-foreground">{it.description}</p>}
        {Object.keys(specs).length > 0 && (
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm border-t border-border pt-3">
            {Object.entries(specs).map(([k, v]) => <div key={k} className="flex justify-between border-b border-border/50 py-1"><span className="text-muted-foreground">{k}</span><span className="font-medium text-right">{v}</span></div>)}
          </div>
        )}
        {req && <RequestDialog prefix={prefix} it={it} type={req} onClose={() => setReq(null)} />}
      </DialogContent>
    </Dialog>
  );
}

function RequestDialog({ prefix, it, type, onClose }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/dev-requests", { prefix, item_id: it.id, type, message: msg });
      toast.success(type === "sample" ? "Sample request sent to our team" : "Question sent to our R&D team");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="portal-request-dialog">
        <DialogHeader>
          <DialogTitle>{type === "sample" ? "Request a Sample" : "Ask a Technical Question"}</DialogTitle>
          <DialogDescription>{it.dev_id} · {it.name}. Our R&D team will get back to you.</DialogDescription>
        </DialogHeader>
        <Textarea rows={4} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder={type === "sample" ? "Any notes on quantity, colourway, timeline… (optional)" : "What would you like to know? (optional)"} data-testid="portal-request-message" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="portal-request-submit">{type === "sample" ? "Send Request" : "Send Question"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NotificationsBell() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const { data: notifs } = useList("my-notifs", "/my-notifications");
  const { data: updates } = useList("my-updates", "/my-updates");
  const { data: uc } = useList("my-updates-unread", "/my-updates/unread-count");
  const restockUnread = (notifs || []).filter((n) => !n.read).length;
  const updatesUnread = uc?.count || 0;
  const total = restockUnread + updatesUnread;
  const markAll = async () => {
    await Promise.all([api.post("/my-updates/read-all").catch(() => {}), api.post("/my-notifications/seen").catch(() => {})]);
    qc.invalidateQueries({ queryKey: ["my-updates"] }); qc.invalidateQueries({ queryKey: ["my-updates-unread"] }); qc.invalidateQueries({ queryKey: ["my-notifs"] });
  };
  const openUpdate = async (u) => {
    setDetail(u);
    if (!u.is_read) { await api.post(`/my-updates/${u.id}/read`).catch(() => {}); qc.invalidateQueries({ queryKey: ["my-updates"] }); qc.invalidateQueries({ queryKey: ["my-updates-unread"] }); }
  };
  return (
    <>
      <Button variant="ghost" size="icon" onClick={() => setOpen(true)} className="relative" data-testid="portal-bell">
        <Bell className="w-[18px] h-[18px]" />
        {total > 0 && <span className="absolute top-1 right-1 min-w-4 h-4 px-1 text-[9px] font-bold bg-destructive text-destructive-foreground rounded-full flex items-center justify-center animate-pulse" data-testid="portal-bell-badge">{total}</span>}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="portal-notifs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2">Notifications {total > 0 && <button onClick={markAll} className="text-xs text-primary hover:underline font-normal" data-testid="portal-mark-all">Mark all as read</button>}</DialogTitle>
            <DialogDescription>Updates from the team and status changes on your requests.</DialogDescription>
          </DialogHeader>
          {(updates || []).length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Updates</div>
              <div className="space-y-1.5">
                {(updates || []).slice(0, 8).map((u) => (
                  <button key={u.id} onClick={() => openUpdate(u)} className={cn("w-full text-left rounded-lg p-2.5 border flex items-start gap-2", u.is_read ? "border-border" : "border-primary/30 bg-primary/5")} data-testid={`bell-update-${u.id}`}>
                    {!u.is_read && <span className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />}
                    <div className="min-w-0 flex-1"><div className="text-sm font-medium truncate flex items-center gap-1.5">{u.title} <PriorityBadge priority={u.priority} /></div><div className="text-xs text-muted-foreground line-clamp-1">{u.description}</div></div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {(notifs || []).length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1 mt-1">Request Activity</div>
              <div className="space-y-1.5">
                {(notifs || []).slice(0, 8).map((n) => (
                  <div key={n.id} className={cn("text-sm rounded-lg p-2.5 border", n.read ? "border-border" : "border-primary/30 bg-primary/5")} data-testid="portal-notif-item">
                    <div>{n.message}</div>
                    <div className="text-[10px] text-muted-foreground mt-1">{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!updates?.length && !notifs?.length && <EmptyState title="No notifications yet" testid="portal-notifs-empty" />}
        </DialogContent>
      </Dialog>
      {detail && <UpdateDetail u={detail} onClose={() => setDetail(null)} />}
    </>
  );
}

function PortalMyRequests() {
  const qc = useQueryClient();
  const seenRef = useRef(new Set());
  const { data: reqs, isLoading } = useList("portal-myreq", "/my-dev-requests");
  useEffect(() => {
    (reqs || []).filter((r) => r.buyer_unread && r.replies?.length && !seenRef.current.has(r.id)).forEach((r) => {
      seenRef.current.add(r.id);
      api.post(`/my-dev-requests/${r.id}/seen`).then(() => qc.invalidateQueries({ queryKey: ["portal-myreq"] })).catch(() => {});
    });
  }, [reqs, qc]);
  if (isLoading) return <Loading />;
  if (!reqs?.length) return <Card className="p-8"><EmptyState title="No requests yet" hint="Request a sample or ask a question from any development — replies from our team appear here." testid="portal-myreq-empty" /></Card>;
  return (
    <div className="space-y-3" data-testid="portal-myreq-list">
      {reqs.map((r) => (
        <Card key={r.id} className="p-4" data-testid={`portal-myreq-${r.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] font-num text-muted-foreground">{r.dev_id}</div>
              <div className="font-semibold text-sm">{r.dev_name}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{r.type === "sample" ? "Sample request" : "Question"} · {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</div>
            </div>
            <StatusBadge status={(r.status || "").replace(/_/g, " ")} />
          </div>
          {r.message && <p className="text-sm mt-2 bg-secondary/50 rounded-lg p-2.5">{r.message}</p>}
          {r.replies?.length > 0 && (
            <div className="mt-3 space-y-2 border-t border-border pt-3">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Replies from our team</div>
              {r.replies.map((rp) => (
                <div key={rp.id} className="bg-primary/8 border border-primary/20 rounded-lg p-2.5" data-testid="portal-myreq-reply">
                  <div className="text-sm">{rp.message}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">{rp.by} · {rp.at ? new Date(rp.at).toLocaleString() : ""}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}
function PortalSpotlight({ wmLabel }) {
  const { data: inks } = useList("portal-innovation", "/innovation");
  const { data: fabrics } = useList("portal-fabrics", "/fabrics");
  const [detail, setDetail] = useState(null);
  const month = new Date().toISOString().slice(0, 7);
  const items = [
    ...(inks || []).map((x) => ({ ...x, _p: "innovation" })),
    ...(fabrics || []).map((x) => ({ ...x, _p: "fabrics" })),
  ].filter((x) => (x.created_at || "").slice(0, 7) === month)
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
    .slice(0, 10);
  if (!items.length) return null;
  return (
    <Card className="p-4 sm:p-5 mb-6 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent" data-testid="portal-spotlight">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
        <div>
          <div className="font-display font-bold text-lg leading-none">New This Month</div>
          <div className="text-xs text-muted-foreground">Fresh inks & fabrics from our R&D team — tap to explore and request a sample</div>
        </div>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
        {items.map((it) => {
          const thumb = it.thumbnail || it.images?.[0];
          return (
            <button key={`${it._p}-${it.id}`} onClick={() => setDetail(it)} className="group shrink-0 w-40 text-left" data-testid={`spotlight-card-${it.dev_id}`}>
              <div className="relative aspect-[4/3] rounded-lg bg-secondary overflow-hidden">
                {thumb ? <img src={thumb} alt="" draggable={false} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" /> : <div className="w-full h-full flex items-center justify-center"><FlaskConical className="w-8 h-8 text-muted-foreground" /></div>}
                {it.confidential && <Watermark label={wmLabel} />}
                <span className="absolute top-1.5 left-1.5 z-20 text-[9px] font-bold bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full tracking-wide">NEW</span>
              </div>
              <div className="mt-1.5">
                <div className="text-[10px] font-num text-muted-foreground">{it._p === "innovation" ? "Ink" : "Fabric"}</div>
                <div className="text-sm font-medium truncate">{it.name}</div>
              </div>
            </button>
          );
        })}
      </div>
      {detail && <PortalDevDetail prefix={detail._p} it={detail} wmLabel={wmLabel} onClose={() => setDetail(null)} />}
    </Card>
  );
}
